// UI
import { CardBody, CardWrapper } from "@/components/Card"
import { Button } from "@/components/Button"
import { Anchor } from "@/components/Anchor"

// Components
import { VoucherInstructions } from "./_components/VoucherInstructions"

// i18n
import { useTrans } from "@/i18n"
import { useLang } from "@/context/useLang"

// Utils
import { copy } from "@/lib/copyClipboard"

// Context
import { useToasts } from "@/context/useToasts"

// Queries
import { useMemberGetMemberVoucherQuery } from "@/api/graphql"

// Icons
import { FiCopy } from "@/lib/icons"

// Environment variables
import { EXTERNAL_PLATFORM_ZONHUB_INVESTMENT } from "@/lib/env"

/**
 * CommunitySharePage
 * @returns
 */
export function CommunitySharePage() {
	// Translations
	const t = useTrans("investor")
	const { formatCurrency } = useLang()

	// Context
	const toasts = useToasts()

	// Campaign info
	const { data } = useMemberGetMemberVoucherQuery()
	const voucher = data?.me?.member_get_member_voucher
	const termsUrl = (voucher as { terms_url?: string | null } | undefined)
		?.terms_url

	// Share full message. Fallback to copy if not supported.
	async function shareFullMessage(
		code: string | null | undefined,
		discount: number | null | undefined,
	) {
		const campaignText = t("investor:community.invites.share.text", {
			code: code,
			discount: discount,
			url: EXTERNAL_PLATFORM_ZONHUB_INVESTMENT,
		})

		if (navigator.share) {
			try {
				await navigator.share({
					text: campaignText,
				})

				// Show toast
				toasts.addToast({
					text: t("investor:community.invites.share.thanks"),
					id: "share-community-share",
				})
			} catch {
				void shareFullMessageViaCopy(
					String(voucher?.code).toUpperCase(),
					voucher?.amount,
				)
			}
		} else {
			void shareFullMessageViaCopy(
				String(voucher?.code).toUpperCase(),
				voucher?.amount,
			)
		}
	}

	/**
	 * copyOnlyCodeViaClipboard
	 */
	const copyOnlyCodeViaClipboard = async (
		code: string | null | undefined,
	) => {
		try {
			await copy(`${code}`)
			toasts.addToast({
				text: t("investor:community.invites.share.success"),
				id: "copy-community-share",
			})
		} catch {
			toasts.addToast({
				text: t("investor:community.invites.share.failed"),
				id: "copy-recovery-codes-failed",
			})
		}
	}

	// shareFullMessageViaCopy
	const shareFullMessageViaCopy = async (
		code: string | null | undefined,
		discount: number | null | undefined,
	) => {
		const campaignText = t("investor:community.invites.share.text", {
			code: code,
			discount: discount,
			url: EXTERNAL_PLATFORM_ZONHUB_INVESTMENT,
		})

		try {
			await copy(campaignText)
			toasts.addToast({
				text: t("investor:community.invites.share.success"),
				id: "copy-community-share",
			})
		} catch {
			toasts.addToast({
				text: t("investor:community.invites.share.failed"),
				id: "copy-recovery-codes-failed",
			})
		}
	}

	return (
		<>
			<CardWrapper>
				<CardBody
					className={
						voucher
							? "grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-12"
							: "flex flex-col gap-5"
					}
				>
					{voucher ? (
						<div className="mx-auto w-full max-w-lg lg:mx-0">
							<div className="relative mb-12 sm:mb-14">
								<div className="absolute inset-x-0 top-[12%] flex justify-center px-4">
									<div className="flex max-w-full items-center gap-2 rounded-lg bg-white p-2 text-center shadow-xs">
										<span className="truncate font-mono text-sm sm:text-base">
											{String(voucher.code).toUpperCase()}
										</span>
										<Button
											variant="transparent"
											size="small"
											aria-label={t(
												"investor:community.invites.share.success",
											)}
											onClick={() =>
												void copyOnlyCodeViaClipboard(
													String(
														voucher.code,
													).toUpperCase(),
												)
											}
										>
											<FiCopy />
										</Button>
									</div>
								</div>

								<img
									src="/assets/sharing/member-get-member.png"
									alt="Network Invites"
									className="block h-auto w-full"
								/>
								<div className="absolute -bottom-8 left-1/2 -translate-x-1/2 sm:-bottom-10">
									<div className="bg-primary-500 flex size-16 items-center justify-center rounded-full text-black sm:size-20">
										<span className="text-center text-xl font-bold sm:text-2xl">
											{formatCurrency(
												Number(voucher.amount),
												{
													maximumFractionDigits: 0,
												},
											)}
										</span>
									</div>
								</div>
							</div>

							<p className="mt-5 text-center">
								{t("investor:community.invites.copy", {
									discount: voucher.amount,
								})}
							</p>
							<div className="mt-5 flex flex-col items-center gap-3">
								<Button
									onClick={() =>
										shareFullMessage(
											voucher.code,
											voucher.amount,
										)
									}
									className="w-full sm:w-auto"
								>
									{t("investor:community.invites.button")}
								</Button>
								{termsUrl ? (
									<Anchor
										href={termsUrl}
										target="_blank"
										rel="noopener noreferrer"
									>
										{t("investor:community.invites.terms")}
									</Anchor>
								) : null}
							</div>
						</div>
					) : null}

					<VoucherInstructions />
				</CardBody>
			</CardWrapper>
		</>
	)
}
